import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { isWithinInterval, parseISO } from 'date-fns';

const { FiFilter, FiX, FiCalendar, FiDollarSign, FiTag, FiRefreshCw, FiChevronDown } = FiIcons;

const AdvancedTransactionFilters = ({ 
  onFilterChange, 
  properties, 
  categories, 
  tags, 
  onClear 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    minAmount: '',
    maxAmount: '',
    propertyId: 'all',
    type: 'all',
    category: 'all',
    selectedTags: []
  });

  const handleChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const toggleTag = (tag) => {
    const newTags = filters.selectedTags.includes(tag)
      ? filters.selectedTags.filter(t => t !== tag)
      : [...filters.selectedTags, tag];
    handleChange('selectedTags', newTags);
  };

  const handleClear = () => {
    const reset = {
      startDate: '',
      endDate: '',
      minAmount: '',
      maxAmount: '',
      propertyId: 'all',
      type: 'all',
      category: 'all',
      selectedTags: []
    };
    setFilters(reset);
    onClear();
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center justify-between">
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all border ${isOpen ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-100' : 'bg-white text-gray-500 border-gray-100 hover:border-indigo-200'}`}
        >
          <SafeIcon icon={FiFilter} />
          {isOpen ? 'Hide Advanced Filters' : 'Advanced Filters'}
          <SafeIcon icon={FiChevronDown} className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {Object.values(filters).some(v => v !== 'all' && v !== '' && (Array.isArray(v) ? v.length > 0 : true)) && (
          <button 
            onClick={handleClear}
            className="flex items-center gap-1.5 text-[10px] font-black text-red-500 uppercase tracking-widest hover:text-red-600 transition-colors"
          >
            <SafeIcon icon={FiRefreshCw} size={12} /> Clear All
          </button>
        )}
      </div>

      {isOpen && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 p-8 bg-white rounded-3xl border border-gray-100 shadow-xl animate-in slide-in-from-top-4 duration-300">
          {/* Date Range */}
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">
              <SafeIcon icon={FiCalendar} className="text-indigo-500" /> Date Range
            </label>
            <div className="flex gap-2">
              <input 
                type="date" 
                className="flex-1 p-3 bg-gray-50 border border-gray-100 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                value={filters.startDate}
                onChange={(e) => handleChange('startDate', e.target.value)}
              />
              <input 
                type="date" 
                className="flex-1 p-3 bg-gray-50 border border-gray-100 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                value={filters.endDate}
                onChange={(e) => handleChange('endDate', e.target.value)}
              />
            </div>
          </div>

          {/* Amount Range */}
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">
              <SafeIcon icon={FiDollarSign} className="text-green-500" /> Amount Range
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">$</span>
                <input 
                  type="number" 
                  placeholder="Min"
                  className="w-full pl-6 p-3 bg-gray-50 border border-gray-100 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                  value={filters.minAmount}
                  onChange={(e) => handleChange('minAmount', e.target.value)}
                />
              </div>
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">$</span>
                <input 
                  type="number" 
                  placeholder="Max"
                  className="w-full pl-6 p-3 bg-gray-50 border border-gray-100 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                  value={filters.maxAmount}
                  onChange={(e) => handleChange('maxAmount', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Type & Category */}
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">
              <SafeIcon icon={FiTag} className="text-purple-500" /> Categorization
            </label>
            <div className="flex flex-col gap-2">
              <select 
                className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                value={filters.propertyId}
                onChange={(e) => handleChange('propertyId', e.target.value)}
              >
                <option value="all">All Properties</option>
                <option value="shared">Shared Portfolio</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <select 
                className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                value={filters.category}
                onChange={(e) => handleChange('category', e.target.value)}
              >
                <option value="all">All Categories</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">
              <SafeIcon icon={FiX} className="text-amber-500" /> Filter by Tags
            </label>
            <div className="flex flex-wrap gap-1.5 max-h-[84px] overflow-y-auto pr-2 custom-scrollbar">
              {tags.map(tag => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border transition-all ${filters.selectedTags.includes(tag) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-400 border-gray-100 hover:border-indigo-200'}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedTransactionFilters;